import { CircularProgress, InputLabel, MenuItem, Select, Typography } from '@material-ui/core';
import { unstable_Box as Box } from '@material-ui/core/Box';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { Event, Tournament } from '@aps-management/primapp-common';

import { Screen } from '_components/core';
import masterClient from '_utils/masterClient';

import RankingTable, { RankingTableLegend } from './RankingTable';
import Header from './_Header';

const TournamentResults = ({ match, golfId }) => {
  const { id } = match.params;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // useRef - on garde la ref sans déclencher de re-renders
  const isFirstLoading = useRef(true);

  const [tournament, setTournament] = useState(null);
  const [isResultsFetching, setIsResultsFetching] = useState(false);
  const [results, setResults] = useState([]);
  const [playerCount, setPlayerCount] = useState(0);

  const resultTypes = { B: 'Brut', N: 'Net' };
  const [selectedType, setSelectedType] = useState('B');

  const [series, setSeries] = useState([]);
  const [selectedSerie, setSelectedSerie] = useState('-1');

  const handleTypeChange = (e) => {
    setSelectedType(e.target.value);
  };
  const handleSerieChange = (e) => {
    setSelectedSerie(e.target.value);
  };

  // Fetch infos compétition (fonction mémorisée)
  const fetchTournament = useCallback(async () => {
    try {
      const response = await Tournament.api.getTournament(masterClient, { id, golfId });
      setTournament(response);
    } catch (err) {
      setError('Impossible de récupérer la compétition.');
    } finally {
      setLoading(false);
    }
  }, [id, golfId]);

  // Fetch resultats (fonction mémorisée)
  const fetchResults = useCallback(async () => {
    try {
      setIsResultsFetching(true);
      const response = await Tournament.api.getResults(masterClient, {
        id,
        golfId,
        type: selectedType,
      });
      setResults(response.results);

      if (isFirstLoading.current) {
        const uniqueSeries = response.results.reduce((acc, { serieName, tournamentType }) => {
          if (acc.find((a) => a.name === serieName)) return acc;

          acc.push({ name: serieName, gameFormula: tournamentType });
          return acc;
        }, []);
        uniqueSeries.sort((a, b) => a.name.localeCompare(b.name));
        setSeries(uniqueSeries);
        isFirstLoading.current = false;
      }
    } catch (err) {
      setError('Impossible de récupérer les résultats.');
    } finally {
      setIsResultsFetching(false);
    }
  }, [id, golfId, selectedType]);

  // Fetch infos compétition au lancement
  useEffect(() => {
    fetchTournament();
  }, [fetchTournament]);

  // Fetch résultats lorsque la compétition est initialisée
  useEffect(() => {
    if (!tournament) return;

    fetchResults();
  }, [tournament, fetchResults]);

  // Regroupe les résultas pas série (tableau mémorisé)
  const resultsBySerie = useMemo(
    () =>
      results.reduce((acc, r) => {
        if (!acc[r.serieName]) acc[r.serieName] = [];
        acc[r.serieName].push(r);

        return acc;
      }, {}),
    [results],
  );

  // Calcule le nombre de joueurs en tout ou pour la série sélectionnée
  useEffect(() => {
    if (selectedSerie === '-1') {
      setPlayerCount(results.length);
    } else {
      setPlayerCount(resultsBySerie[selectedSerie]?.length || 0);
    }
  }, [selectedSerie, results, series, resultsBySerie]);

  // Affiche les tableaux de résultats
  const renderRankingTables = () => {
    const props = {
      resultType: resultTypes[selectedType],
      numberOfRounds: tournament.numberOfRounds,
    };

    // Si la compétition est par équipe, on ne filtre pas par série.
    // Tout le monde dans le même tableau
    if (tournament.isTeamFormat) {
      return <RankingTable results={results} {...props} />;
    }

    return series.map((serie) => {
      // Si une série est sélectionné et différente, alors on ne l'affiche pas
      if (selectedSerie !== '-1' && selectedSerie !== serie.name) return null;

      return <RankingTable key={serie.name} serie={serie} results={resultsBySerie[serie.name] || []} {...props} />;
    });
  };

  const renderContent = () => {
    if (!tournament) return null;

    return (
      <>
        <Header tournament={tournament} />
        <Box p={2} mb={2} display='flex' flexDirection='row' justifyContent='flex-end'>
          <Box mr={2} flex={2} display='flex' flexDirection='column' justifyContent='space-around'>
            {!tournament.isTeamFormat && series.length > 1 && (
              <>
                <InputLabel shrink>{'Série'}</InputLabel>
                <Select label='Série' value={selectedSerie} onChange={handleSerieChange}>
                  <MenuItem value='-1'>{'Toutes'}</MenuItem>
                  {series.map(({ name }, i) => (
                    <MenuItem key={i} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </>
            )}
          </Box>
          <Box flex={1} display='flex' flexDirection='column' justifyContent='space-around'>
            <InputLabel shrink>{'Résultat'}</InputLabel>
            <Select label='Type' value={selectedType} onChange={handleTypeChange}>
              {Object.entries(resultTypes).map(([value, name]) => (
                <MenuItem key={value} value={value}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
        <RankingTableLegend />
        {isResultsFetching ? (
          <Box display='flex' justifyContent='center'>
            <CircularProgress size={24} color='secondary' />
          </Box>
        ) : (
          <>
            {playerCount > 1 && (
              <Typography gutterBottom align='center' variant='h6'>
                {playerCount} joueurs
              </Typography>
            )}
            {renderRankingTables()}
          </>
        )}
      </>
    );
  };

  return (
    <Screen error={error} loading={loading} title='Compétitions - Résultats'>
      {renderContent()}
    </Screen>
  );
};

const mapStateToProps = ({ app: { golf } }) => ({ golfId: golf.id });

export default connect(mapStateToProps, Event.actions)(TournamentResults);
